import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Card,
    Col,
    DatePicker,
    Form,
    InputNumber,
    Modal,
    Row,
    Select,
    Space,
    Table,
    Tag,
    Typography,
    message,
} from "antd";
import dayjs from "dayjs";
import {
    getInstitutions,
    getTransactionScheduleBankOptionsByInstitution,
    getTransactionScheduleUnagreedReservations,
} from "../../api/transactionScheduleApi";
import { formatAmount } from "./transactionScheduleUi";

const { Text } = Typography;

function normalizeInstitutionName(value = "") {
    return String(value)
        .toUpperCase()
        .replace(/\bLLC\b/g, "ХХК")
        .replace(/\bLTD\b/g, "")
        .replace(/\bINC\b/g, "")
        .replace(/\bCO\b/g, "")
        .replace(/[^\p{L}\p{N}\s]/gu, " ")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/\bХХК\b/g, "")
        .trim();
}

function findBestInstitutionIdFromRows(rows = [], institutions = []) {
    const payerValues = rows.map((r) => r.payer).filter(Boolean);

    for (const payer of payerValues) {
        const normalizedPayer = normalizeInstitutionName(payer);
        if (!normalizedPayer) continue;

        const exact = institutions.find(
            (item) => normalizeInstitutionName(item?.name) === normalizedPayer
        );
        if (exact) return exact.id;

        const contains = institutions.find((item) => {
            const normalizedInstitution = normalizeInstitutionName(item?.name);
            return (
                normalizedInstitution &&
                (normalizedInstitution.includes(normalizedPayer) ||
                    normalizedPayer.includes(normalizedInstitution))
            );
        });
        if (contains) return contains.id;
    }

    return undefined;
}

export default function BulkManagePreviewModal({
    open,
    rows = [],
    value,
    onChange,
    onCancel,
    onConfirm,
}) {
    const [form] = Form.useForm();

    const [institutions, setInstitutions] = useState([]);
    const [bankOptions, setBankOptions] = useState([]);
    const [reservationData, setReservationData] = useState(null);
    const [loadingInitial, setLoadingInitial] = useState(false);

    const baseManualFilledAt = Form.useWatch("baseManualFilledAt", form);
    const institutionId = Form.useWatch("institutionId", form);

    const totalAmount = useMemo(() => {
        return (rows || []).reduce((sum, r) => sum + Number(r.paymentAmount || 0), 0);
    }, [rows]);

    const currency = rows?.[0]?.currency || "";

    useEffect(() => {
        if (!open) return;

        const loadInitial = async () => {
            try {
                setLoadingInitial(true);

                const institutionList = await getInstitutions();
                setInstitutions(institutionList || []);

                const suggestedInstitutionId =
                    value?.institutionId ??
                    findBestInstitutionIdFromRows(rows, institutionList || []);

                form.setFieldsValue({
                    institutionId: suggestedInstitutionId,
                    baseManualFilledAt: value?.baseManualFilledAt
                        ? dayjs(value.baseManualFilledAt)
                        : dayjs(),
                    manualFee: value?.manualFee ?? 50,
                    swiftFee: value?.swiftFee ?? 100,
                    originalBankAccountId: value?.originalBankAccountId ?? undefined,
                    manualBankAccountId: value?.manualBankAccountId ?? undefined,
                    pdfBankOption: value?.pdfBankOption ?? "golomt",
                });

                if (suggestedInstitutionId) {
                    const [options, reservations] = await Promise.all([
                        getTransactionScheduleBankOptionsByInstitution(
                            suggestedInstitutionId,
                            currency
                        ),
                        getTransactionScheduleUnagreedReservations(
                            rows?.[0]?.id,
                            suggestedInstitutionId,
                            currency
                        ),
                    ]);

                    setBankOptions(options || []);
                    setReservationData(reservations || null);

                    const bestCandidate = (options || [])
                        .map((item) => {
                            const wallet = Number(item?.walletBalance || 0);
                            const reservedRow =
                                (reservations?.manualBankAccountUnagreedAmounts || []).find(
                                    (x) => x.bankAccountId === item.id
                                );
                            const reserved = Number(reservedRow?.amount || 0);
                            const available = wallet - reserved;

                            return { id: item.id, available };
                        })
                        .filter((x) => x.available >= totalAmount)
                        .sort((a, b) => b.available - a.available)[0];

                    const firstOriginalId = value?.originalBankAccountId ?? options?.[0]?.id ?? undefined;
                    const bestManualId = value?.manualBankAccountId ?? bestCandidate?.id ?? options?.[0]?.id ?? undefined;

                    form.setFieldsValue({
                        originalBankAccountId: firstOriginalId,
                        manualBankAccountId: bestManualId,
                    });
                } else {
                    setBankOptions([]);
                    setReservationData(null);
                }
            } catch (error) {
                message.error(error.message || "Олноор засах урьдчилсан мэдээлэл ачаалж чадсангүй");
            } finally {
                setLoadingInitial(false);
            }
        };

        loadInitial();
    }, [open, rows, value, form, currency]);

    const institutionOptions = useMemo(
        () =>
            (institutions || []).map((item) => ({
                label: item.name,
                value: item.id,
            })),
        [institutions]
    );

    const totalInstitutionWalletBalance = useMemo(() => {
        return (bankOptions || []).reduce((sum, item) => {
            const value = Number(item?.walletBalance || 0);
            return sum + (Number.isFinite(value) ? value : 0);
        }, 0);
    }, [bankOptions]);

    const institutionReservedUnagreedTotal = Number(
        reservationData?.institutionUnagreedAmountTotal || 0
    );

    const institutionAvailableToAllocate =
        totalInstitutionWalletBalance - institutionReservedUnagreedTotal;

    const institutionWalletEnough =
        !institutionId || institutionAvailableToAllocate >= totalAmount;

    const institutionWalletShortAmount = Math.max(
        totalAmount - institutionAvailableToAllocate,
        0
    );

    const previewData = useMemo(() => {
        const base = baseManualFilledAt ? dayjs(baseManualFilledAt) : null;

        return (rows || []).map((r, idx) => ({
            key: r.id,
            id: r.id,
            requestNumber: r.requestNumber,
            payer: r.payer,
            paymentAmount: r.paymentAmount,
            currency: r.currency,
            computedManualFilledAt: base
                ? base.add(idx * 5, "second").format("YYYY-MM-DD HH:mm:ss")
                : "-",
        }));
    }, [rows, baseManualFilledAt]);

    const columns = [
        { title: "ID", dataIndex: "id", width: 90 },
        { title: "Хүсэлт", dataIndex: "requestNumber", width: 160 },
        { title: "Төлөгч", dataIndex: "payer", width: 220 },
        {
            title: "Дүн",
            key: "paymentAmount",
            width: 140,
            render: (_, record) =>
                `${formatAmount(record.paymentAmount)} ${record.currency || ""}`.trim(),
        },
        {
            title: "Manual filled at",
            dataIndex: "computedManualFilledAt",
            width: 220,
            render: (v) => <Tag color="blue">{v}</Tag>,
        },
    ];

    const handleInstitutionChange = async (nextInstitutionId) => {
        form.setFieldsValue({
            originalBankAccountId: undefined,
            manualBankAccountId: undefined,
        });

        if (!nextInstitutionId) {
            setBankOptions([]);
            setReservationData(null);
            return;
        }

        try {
            const [options, reservations] = await Promise.all([
                getTransactionScheduleBankOptionsByInstitution(nextInstitutionId, currency),
                getTransactionScheduleUnagreedReservations(
                    rows?.[0]?.id,
                    nextInstitutionId,
                    currency
                ),
            ]);

            setBankOptions(options || []);
            setReservationData(reservations || null);

            const bestManualCandidate = (options || [])
                .map((item) => {
                    const wallet = Number(item?.walletBalance || 0);
                    const reservedRow =
                        (reservations?.manualBankAccountUnagreedAmounts || []).find(
                            (x) => x.bankAccountId === item.id
                        );
                    const reserved = Number(reservedRow?.amount || 0);
                    const available = wallet - reserved;

                    return { id: item.id, available };
                })
                .filter((x) => x.available >= totalAmount)
                .sort((a, b) => b.available - a.available)[0];

            const firstOriginalId = options?.[0]?.id;
            const manualId = bestManualCandidate?.id ?? options?.[0]?.id;

            form.setFieldsValue({
                originalBankAccountId: form.getFieldValue("originalBankAccountId") ?? firstOriginalId,
                manualBankAccountId: form.getFieldValue("manualBankAccountId") ?? manualId,
            });
        } catch (error) {
            message.error(error.message || "Банкны сонголтууд ачаалж чадсангүй");
        }
    };

    const submit = async () => {
        const values = await form.validateFields();

        const payload = {
            institutionId: values.institutionId,
            originalBankAccountId: values.originalBankAccountId,
            manualBankAccountId: values.manualBankAccountId,
            baseManualFilledAt: values.baseManualFilledAt.format("YYYY-MM-DDTHH:mm:ss"),
            manualFee: values.manualFee,
            swiftFee: values.swiftFee,
            pdfBankOption: values.pdfBankOption || "golomt",
        };
        if (!institutionWalletEnough) {
            message.error("Байгууллагын боломжит хийсвэр үлдэгдэл хүрэлцэхгүй байна");
            return;
        }

        if (!form.getFieldValue("originalBankAccountId")) {
            message.error("Үндсэн банкны данс сонгоно уу");
            return;
        }

        if (!form.getFieldValue("manualBankAccountId")) {
            message.error("Гараар сонгосон данс сонгоно уу");
            return;
        }

        onChange?.(payload);
        onConfirm?.(payload);
    };


    const manualReservedMap = useMemo(() => {
        const map = new Map();

        (reservationData?.manualBankAccountUnagreedAmounts || []).forEach(
            (item) => {
                map.set(item.bankAccountId, Number(item.amount || 0));
            }
        );

        return map;
    }, [reservationData]);


    const bankSelectOptions = useMemo(() => {
        return (bankOptions || []).map((item) => {
            const bankName =
                item?.bank?.displayName ||
                item?.bankDisplayName ||
                item?.bankName ||
                item?.bank ||
                "-";

            const currency =
                item?.currencyCode ||
                item?.currency?.code ||
                item?.currency?.name ||
                item?.currency ||
                "-";

            const accountNumber = item?.accountNumber || "-";

            const walletBalance = Number(item?.walletBalance || 0);
            const reserved = Number(manualReservedMap.get(item.id) || 0);
            const available = walletBalance - reserved;

            return {
                label:
                    `${bankName} | ${currency} | ${accountNumber}` +
                    ` | Хийсвэр: ${formatAmount(walletBalance)}` +
                    ` | Боломжит: ${formatAmount(available)}`,
                value: item.id,
                bankName,
                currency,
                available,
            };
        });
    }, [bankOptions, manualReservedMap]);
    const bestManualBankAccountId = useMemo(() => {
        const candidates = (bankOptions || [])
            .map((item) => {
                const wallet = Number(item?.walletBalance || 0);
                const reserved = Number(manualReservedMap.get(item.id) || 0);
                const available = wallet - reserved;

                return {
                    id: item.id,
                    available,
                };
            })
            .filter((x) => x.available >= totalAmount)
            .sort((a, b) => b.available - a.available);

        return candidates[0]?.id;
    }, [bankOptions, manualReservedMap, totalAmount]);

    const canSubmit =
        !!institutionId &&
        institutionWalletEnough &&
        !!form.getFieldValue("originalBankAccountId") &&
        !!form.getFieldValue("manualBankAccountId");


    return (
        <Modal
            title="Олноор засах – Урьдчилсан харах"
            open={open}
            onCancel={onCancel}
            onOk={submit}
            okButtonProps={{ disabled: !canSubmit }}
            okText="Хадгалах"
            cancelText="Болих"
            width={1100}
            confirmLoading={loadingInitial}
        >
            <Space direction="vertical" style={{ width: "100%" }} size={16}>
                <Card loading={loadingInitial}>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={6}>
                            <Text strong>Сонгосон мөр</Text>
                            <div>{rows.length}</div>
                        </Col>
                        <Col xs={24} md={6}>
                            <Text strong>Нийт дүн</Text>
                            <div>
                                {formatAmount(totalAmount)} {currency}
                            </div>
                        </Col>
                        <Col xs={24} md={6}>
                            <Text strong>Боломжит хийсвэр үлдэгдэл</Text>
                            <div>
                                {formatAmount(institutionAvailableToAllocate)} {currency}
                            </div>
                        </Col>
                        <Col xs={24} md={6}>
                            <Text strong>Дутуу</Text>
                            <div style={{ color: institutionWalletEnough ? "#389e0d" : "#cf1322" }}>
                                {formatAmount(institutionWalletShortAmount)} {currency}
                            </div>
                        </Col>
                    </Row>
                </Card>

                {!institutionWalletEnough && institutionId ? (
                    <Alert
                        type="warning"
                        showIcon
                        message="Байгууллагын боломжит хийсвэр үлдэгдэл хүрэлцэхгүй байна"
                        description={
                            <div>
                                Боломжит: <strong>{formatAmount(institutionAvailableToAllocate)} {currency}</strong>
                                {" | "}
                                Шаардлагатай: <strong>{formatAmount(totalAmount)} {currency}</strong>
                            </div>
                        }
                    />
                ) : null}

                <Form form={form} layout="vertical">
                    <Row gutter={[16, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item
                                name="institutionId"
                                label="Байгууллага"
                                rules={[{ required: true, message: "Байгууллага сонгоно уу" }]}
                            >
                                <Select
                                    showSearch
                                    optionFilterProp="label"
                                    options={institutionOptions}
                                    onChange={handleInstitutionChange}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24} md={8}>
                            <Form.Item
                                name="baseManualFilledAt"
                                label="Base огноо, цаг"
                                rules={[{ required: true, message: "Base огноо сонгоно уу" }]}
                            >
                                <DatePicker
                                    showTime
                                    style={{ width: "100%" }}
                                    format="YYYY-MM-DD HH:mm:ss"
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="originalBankAccountId"
                                label="Үндсэн банкны данс"
                                rules={[{ required: true, message: "Үндсэн банкны данс сонгоно уу" }]}
                            >
                                <Select
                                    showSearch
                                    optionFilterProp="label"
                                    options={bankSelectOptions}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24} md={12}>
                            <Form.Item
                                name="manualBankAccountId"
                                label="Гараар сонгосон данс"
                                rules={[{ required: true, message: "Гараар сонгосон данс сонгоно уу" }]}
                            >
                                <Select
                                    showSearch
                                    optionFilterProp="label"
                                    options={bankSelectOptions}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24} md={4}>
                            <Form.Item
                                name="manualFee"
                                label="Manual fee"
                                rules={[{ required: true, message: "Manual fee оруулна уу" }]}
                            >
                                <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
                            </Form.Item>
                        </Col>

                        <Col xs={24} md={4}>
                            <Form.Item
                                name="swiftFee"
                                label="Swift fee"
                                rules={[{ required: true, message: "Swift fee оруулна уу" }]}
                            >
                                <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
                            </Form.Item>
                        </Col>

                        <Col xs={24} md={4}>
                            <Form.Item
                                name="pdfBankOption"
                                label="PDF банк"
                                rules={[{ required: true, message: "PDF банк сонгоно уу" }]}
                            >
                                <Select
                                    size="small"
                                    options={[
                                        { value: "golomt", label: "Голомт" },
                                        { value: "khas", label: "ХАС" },
                                    ]}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>

                <Card size="small">
                    <Text strong>Банкны сонголтууд</Text>
                    <div style={{ marginTop: 8 }}>
                        {bankOptions.length ? (
                            bankOptions.map((opt) => {
                                const bankName = opt.bank?.displayName || opt.bank || "-";
                                const currencyCode = opt.currencyCode || opt.currency || "-";
                                const accountNumber = opt.accountNumber || "-";
                                const walletBalance = Number(opt.walletBalance || 0);
                                const reserved = 0;
                                const available = walletBalance - reserved;

                                return (
                                    <div key={opt.id}>
                                        {`${bankName} | ${currencyCode} | ${accountNumber} | Хийсвэр: ${formatAmount(walletBalance)} | Боломжит: ${formatAmount(available)}`}
                                    </div>
                                );
                            })
                        ) : (
                            <Text type="secondary">Банкны сонголт байхгүй байна</Text>
                        )}
                    </div>
                </Card>

                <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={previewData}
                    pagination={false}
                    scroll={{ x: 900, y: 360 }}
                    size="small"
                />
            </Space>
        </Modal>
    );
}